import { useCallback, useState } from "react";
import type { ImportAppId, ImportStepContext } from "./import-types";
import { AppleNotesStepsMap } from "./apple-notes-steps";
import { ObsidianStepsMap } from "./obsidian-steps";
import { OtherAppsStepsMap } from "./other-apps-steps";
import { getFilteredNotesApps } from "./import-steps.config";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

type ImportScreen = "apple-notes-1" | "apple-notes-one-click" | "obsidian-1" | "other-apps-1";

const getDefaultScreen = (): { screen: ImportScreen; appId: ImportAppId } => {
  const firstApp = getFilteredNotesApps()[0];
  switch (firstApp?.id) {
    case "apple-notes":
      return { screen: "apple-notes-1", appId: "apple-notes" };
    case "obsidian":
      return { screen: "obsidian-1", appId: "obsidian" };
    default:
      return { screen: "other-apps-1", appId: "other" };
  }
};

export type ImportPlacement = "onboarding" | "profile-menu";

interface ImportNotesProps {
  placement: ImportPlacement;
  onClose: () => void;
}

/**
 * Self-contained import notes flow. Starts directly on the first available
 * app tab (Apple Notes on macOS, Obsidian otherwise). Tabs handle switching.
 *
 * Used from the profile-menu modal — onClose closes the modal.
 */
export const ImportNotes: React.FC<ImportNotesProps> = ({ placement, onClose }) => {
  const [defaults] = useState(getDefaultScreen);
  const [currentScreen, setCurrentScreen] = useState<ImportScreen>(defaults.screen);
  const [selectedAppId, setSelectedAppId] = useState<ImportAppId | null>(defaults.appId);
  const [customAppName, setCustomAppName] = useState("");

  const context: ImportStepContext = {
    placement,
    selectedAppId,
    customAppName,
  };

  const handleUpdateContext = useCallback((updates: Partial<ImportStepContext>) => {
    if (updates.selectedAppId !== undefined) {
      setSelectedAppId(updates.selectedAppId as ImportAppId | null);
    }
    if (updates.customAppName !== undefined) {
      setCustomAppName(updates.customAppName as string);
    }
  }, []);

  const handleNavToStep = useCallback(
    (step: string) => {
      trackEvent(TrackedEvent.ImportWizardStepChange, {
        from: currentScreen,
        to: step,
        appId: selectedAppId,
      });
      setCurrentScreen(step as ImportScreen);
    },
    [currentScreen, selectedAppId]
  );

  const handleTabSelect = useCallback(
    (appId: ImportAppId) => {
      let nextScreen: ImportScreen = "other-apps-1";

      switch (appId) {
        case "apple-notes":
          nextScreen = "apple-notes-1";
          break;
        case "obsidian":
          nextScreen = "obsidian-1";
          break;
        case "other":
        default:
          nextScreen = "other-apps-1";
          break;
      }

      setSelectedAppId(
        appId === "apple-notes" || appId === "obsidian" || appId === "other" ? appId : "other"
      );
      handleNavToStep(nextScreen);
    },
    [handleNavToStep]
  );

  // Build the common props that all import step components expect
  const stepProps = {
    context,
    onUpdateContext: handleUpdateContext,
    onNavToStep: handleNavToStep,
    handleTabSelect,
    onClose,
    selectedAppId,
    customAppName,
    onSetSelectedApp: setSelectedAppId,
    onSetCustomAppName: setCustomAppName,
  };

  // Render the step map entries for the current screen
  const renderStep = () => {
    switch (currentScreen) {
      case "apple-notes-1": {
        const config = AppleNotesStepsMap["apple-notes-1"];
        return config.render(stepProps);
      }
      case "apple-notes-one-click": {
        const config = AppleNotesStepsMap["apple-notes-one-click"];
        return config.render(stepProps);
      }
      case "obsidian-1": {
        const config = ObsidianStepsMap["obsidian-1"];
        return config.render(stepProps);
      }
      case "other-apps-1": {
        const config = OtherAppsStepsMap["other-apps-1"];
        return config.render(stepProps);
      }
      default:
        return null;
    }
  };

  const stepContent = renderStep();

  // apple-notes-one-click owns its own container (MDSDialog)
  if (currentScreen === "apple-notes-one-click") {
    return <>{stepContent}</>;
  }

  return <>{stepContent}</>;
};
